import React, { useState } from "react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import InfoPage from ".";
import { CHECKOUT_TRIAL_TOOLTIP } from "../../utils/checkoutTrialTooltip";
import useTrustedCarouselProfiles from "../../hooks/useTrustedCarouselProfiles";
import IntroVideoPreview from "../../components/IntroVideoPreview";
import "./TravelerSignup.sass";

const TravelerSignup = () => {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [instagramHandle, setInstagramHandle] = useState("");
  const [followingPostaway, setFollowingPostaway] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('signup'); // 'signup' | 'plan'
  const trustedProfiles = useTrustedCarouselProfiles();
  const profileCount = Math.max(trustedProfiles.length, 1);
  const travelerDesktopDurationSeconds = Math.min(520, Math.max(52, Math.round(profileCount * 3.8)));
  const travelerMobileDurationSeconds = Math.min(620, Math.max(68, Math.round(profileCount * 4.6)));
  const travelerCarouselTimingVars = {
    "--checkout-carousel-duration-desktop": `${travelerDesktopDurationSeconds}s`,
    "--checkout-carousel-duration-mobile": `${travelerMobileDurationSeconds}s`,
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email || !firstName || !lastName || !password) {
      setErrorMessage("Please fill in all fields");
      return;
    }
    if (password.length < 8) {
      setErrorMessage("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      return;
    }
    
    setSubmitting(true);
    setErrorMessage("");
    
    try {
      localStorage.setItem('postaway_email', email);
      localStorage.setItem('postaway_user_type', 'traveler');
      
      const signupRes = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'traveler',
          firstName,
          lastName,
          email,
          password,
          instagramHandle: instagramHandle || null,
          followingPostaway,
          membershipTier: 'basic', // Will be upgraded if they pick All Access
        }),
      });
      
      if (!signupRes.ok) {
        throw new Error('Signup failed');
      }
      
      const signupData = await signupRes.json();
      if (signupData.token) {
        localStorage.setItem('postaway_auth_token', signupData.token);
      }
      if (signupData.user) {
        localStorage.setItem('postaway_user_data', JSON.stringify(signupData.user));
      }
      
      setStep('plan');
    } catch (error) {
      console.error('Signup error:', error);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBasicPlan = () => {
    window.location.href = '/hosts';
  };

  const handleAllAccessPlan = async () => {
    setSubmitting(true);
    setErrorMessage("");
    try {
      const checkoutRes = await fetch('/api/traveler/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const checkoutData = await checkoutRes.json();
      
      if (checkoutData.ok && checkoutData.url) {
        window.location.href = checkoutData.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setErrorMessage("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const planSelection = (
    <div className="traveler-plan-selection" style={{ gridColumn: '1 / -1' }}>
      <h2 className="traveler-plan-title">Choose Your Membership</h2>
      <p className="traveler-plan-subtitle">Select the plan that works best for you.</p>
      
      <div className="traveler-plan-cards">
        <div className="traveler-plan-card traveler-plan-basic">
          <div className="traveler-plan-card-header">
            <h3>Basic Membership</h3>
            <div className="traveler-plan-price">Free</div>
          </div>
          <ul className="traveler-plan-features">
            <li>Access to property listings</li>
            <li>Direct booking sites — save 15-20%</li>
          </ul>
          <button
            type="button"
            className="traveler-plan-btn traveler-plan-btn-basic"
            onClick={handleBasicPlan}
            disabled={submitting}
          >
            Get Started Free
          </button>
        </div>
        
        <div className="traveler-plan-card traveler-plan-allaccess">
          <div className="traveler-plan-badge">Recommended</div>
          <div className="traveler-plan-card-header">
            <h3>All Access</h3>
            <div className="traveler-plan-price">$29<span>/year</span></div>
            <div className="traveler-plan-trial">7-day free trial</div>
          </div>
          <ul className="traveler-plan-features">
            <li>Access to property listings</li>
            <li>Free itineraries from travel creators</li>
            <li>Last minute availability</li>
            <li>Exclusive discount codes</li>
            <li>Upcoming events</li>
            <li>Message hosts directly</li>
            <li>Creator storefronts &amp; packing lists</li>
          </ul>
          <Tippy content={CHECKOUT_TRIAL_TOOLTIP} placement="top" maxWidth={380}>
            <div style={{ width: "100%" }}>
              <button
                type="button"
                className="traveler-plan-btn traveler-plan-btn-allaccess"
                onClick={handleAllAccessPlan}
                disabled={submitting}
              >
                {submitting ? "Processing..." : "Start Free Trial"}
              </button>
            </div>
          </Tippy>
        </div>
      </div>
      
      {errorMessage && (
        <div className="login-error" style={{ textAlign: 'center', marginTop: 16 }}>{errorMessage}</div>
      )}
    </div>
  );

  const signupForm = (
    <>
      <div className="traveler-signup-left">
        <div className="traveler-signup-content">
          <h2 className="traveler-signup-title">
            Join as a Traveler
          </h2>
          <p className="traveler-signup-description">
            Discover curated itineraries, unique destinations and exclusive destinations.
          </p>

          <div className="traveler-trusted-carousel trusted-carousel" aria-label="Trusted hosts and creators">
            <div className="trusted-carousel-title">Trusted by top travel creators, photographers and hosts in the hospitality industry.</div>
            <div className="trusted-carousel-viewport">
              <div className="trusted-carousel-track" style={travelerCarouselTimingVars}>
                {[...trustedProfiles, ...trustedProfiles].map((profile, idx) => (
                  <div className="trusted-carousel-item" key={`${profile.handle}-${idx}`}>
                    {profile.introVideoUrl ? (
                      <IntroVideoPreview
                        className="trusted-carousel-avatar"
                        mode="card"
                        posterUrl={profile.introVideoPosterUrl || profile.image}
                        publicId={profile.introVideoPublicId}
                        fullUrl={profile.introVideoUrl}
                        fallbackImageUrl={profile.image}
                        alt={profile.handle}
                      />
                    ) : (
                      <img
                        src={profile.image}
                        alt={profile.handle}
                        className="trusted-carousel-avatar"
                        loading="lazy"
                      />
                    )}
                    <span className="trusted-carousel-handle">{profile.handle}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="traveler-signup-benefits">
            <div className="traveler-benefit-item">✈️ Access to Top Travel Creators</div>
            <div className="traveler-benefit-item">🗺️ Access to Group Trips from your Favorite Creators</div>
            <div className="traveler-benefit-item">🏠 Access to Vacation Rental Properties</div>
            <div className="traveler-benefit-item">📍 Access to Direct Booking Sites (no extra fees)</div>
            <div className="traveler-benefit-item">💰 Exclusive Travel Deals and Discounts</div>
            <div className="traveler-benefit-item">📝 Travel planning tools and checklists</div>
          </div>
          
          <div className="traveler-signup-pricing">
            <div className="traveler-pricing-amount">Free to join — or $29/year for All Access</div>
            <div className="traveler-pricing-note">
              Basic membership is free. All Access includes a 7-day free trial.
            </div>
          </div>
          
          <div className="traveler-signup-testimonial">
            <p className="traveler-testimonial-text">
              "Postaway's itineraries saved me hours of planning!"
            </p>
            <div className="traveler-testimonial-author">
              - Sarah M., Traveler
            </div>
          </div>
        </div>
      </div>

      <div className="login-right">
        <form onSubmit={handleSignup} className="login-form">
          <h3 className="login-title">Start Your Journey!</h3>

          <div className="login-input-group">
            <input
              type="text"
              placeholder=""
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              className="login-input"
              disabled={submitting}
            />
            <label className="login-label">First Name</label>
          </div>

          <div className="login-input-group">
            <input
              type="text"
              placeholder=""
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              className="login-input"
              disabled={submitting}
            />
            <label className="login-label">Last Name</label>
          </div>

          <div className="login-input-group">
            <input
              type="email"
              placeholder=""
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login-input"
              disabled={submitting}
            />
            <label className="login-label">Email Address</label>
          </div>

          <div className="login-input-group">
            <input
              type="text"
              placeholder=""
              value={instagramHandle}
              onChange={(e) => setInstagramHandle(e.target.value)}
              className="login-input"
              disabled={submitting}
            />
            <label className="login-label">Instagram Handle (optional)</label>
          </div>

          <div className="login-checkbox-group">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                checked={followingPostaway}
                onChange={(e) => setFollowingPostaway(e.target.checked)}
                disabled={submitting}
              />
              <span>I am following <strong>@postawaytravels</strong> on Instagram</span>
            </label>
          </div>

          <div className="login-input-group">
            <div className="login-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder=""
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input login-input-password"
                disabled={submitting}
              />
              <button type="button" onClick={() => setShowPassword(s => !s)} aria-label="Toggle password visibility" aria-pressed={showPassword} className="login-password-toggle">
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
                    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
                    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
                    <path d="m2 2 20 20" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <label className="login-label">Password</label>
          </div>

          {password && (
            <div className="login-input-group">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder=""
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="login-input"
                disabled={submitting}
                style={{
                  borderBottom: `2px solid ${confirmPassword && confirmPassword !== password ? '#dc1d08' : 'rgba(0,0,0,.12)'}`
                }}
              />
              <label className="login-label">Confirm Password</label>
            </div>
          )}
          
          {errorMessage && (
            <div className="login-error">{errorMessage}</div>
          )}
          
          <button type="submit" disabled={submitting} className="login-submit">
            {submitting ? "Processing..." : "Continue"}
          </button>

          <div className="login-signup-link">
            Already have an account? <a href="/traveler-login">Login</a>
          </div>
          
          <div className="login-agreement">
            By subscribing to Postaway and becoming a member, I will abide by and be bound by the{' '}
            <a href="/member-agreement">Postaway Terms of Use</a> and <a href="/privacy-policy">Privacy Policy</a>.
          </div>
        </form>
      </div>
    </>
  );

  const belowContent = step === 'plan' ? planSelection : signupForm;

  if (step === 'plan') {
    return <InfoPage below={planSelection} disableStamps tallOverride={false} customMinHeight={900} />;
  }

  return <InfoPage belowContent={signupForm} divider disableStamps />;
};

export default TravelerSignup;
